import {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Modal, Icon} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import './viewRackModal.scss';

const ViewRackModal = ({rack, onClose, onEdit}) => {
  const images = rack.imageUrls || [];
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = useCallback(
    () => setActiveIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1)),
    [images.length]
  );

  const handleNext = useCallback(
    () => setActiveIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0)),
    [images.length]
  );

  const detailFields = [
    [
      {label: 'Material Code', value: rack.material},
      {label: 'Category', value: rack.category},
      {label: 'Capacity', value: rack.capacity}
    ],
    [
      {label: 'Width', value: rack.width},
      {label: 'Depth', value: rack.depth},
      {label: 'Height', value: rack.height}
    ]
  ];

  return (
    <Modal
      id='view-rack'
      type={MODAL_TYPE.SMALL}
      showModal
      title={rack.displayName || ''}
      isDark
      hideCloseButton={false}
      className='viewRackModal'
      primaryButtonText='Edit'
      primaryButtonProps={{iconLeading: 'edit', 'aria-label': 'Edit'}}
      showSecondaryButton={false}
      onCloseModal={onClose}
      onClickSecondaryButton={onClose}
      onClickPrimaryButton={() => onEdit(rack)}
      content={
        <div className='viewRackContent'>
          <div className='viewRackId'>ID: {rack.rackId}</div>

          {images.length > 0 && (
            <>
              <div className='viewRackImagesLabel'>Images</div>
              <div className='viewRackImageContainer'>
                <img
                  src={images[activeIndex]}
                  alt={`${rack.displayName} ${activeIndex + 1}`}
                />
              </div>
              {images.length > 1 && (
                <div className='viewRackCarouselControls'>
                  <button
                    type='button'
                    className='carouselArrow'
                    onClick={handlePrev}
                    data-testid='view-rack-carousel-prev'>
                    <Icon icon='chevron_left' size='small' isDark />
                  </button>
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      type='button'
                      className={`carouselDot${idx === activeIndex ? ' active' : ''}`}
                      onClick={() => setActiveIndex(idx)}
                    />
                  ))}
                  <button
                    type='button'
                    className='carouselArrow'
                    onClick={handleNext}
                    data-testid='view-rack-carousel-next'>
                    <Icon icon='chevron_right' size='small' isDark />
                  </button>
                </div>
              )}
            </>
          )}

          {rack.description && (
            <div className='viewRackSection'>
              <div className='viewRackSectionLabel'>Description</div>
              <div className='viewRackSectionValue viewRackDescription'>
                {rack.description}
              </div>
            </div>
          )}

          {detailFields.map((row, rowIndex) => (
            <div key={rowIndex} className='viewRackFieldRow'>
              {row.map(({label, value}) => (
                <div key={label} className='viewRackField'>
                  <div className='viewRackFieldLabel'>{label}</div>
                  <div className='viewRackFieldValue'>{value || '-'}</div>
                </div>
              ))}
            </div>
          ))}

          <div className='viewRackSection viewRackPointsSection'>
            <div className='viewRackSectionLabel'>Points for the rack</div>
            <div className='viewRackSectionValue'>
              {rack.pointsForRack != null
                ? `${rack.pointsForRack} Points`
                : '-'}
            </div>
          </div>
        </div>
      }
    />
  );
};

ViewRackModal.propTypes = {
  rack: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default ViewRackModal;
